import { useCallback, useEffect, useState } from 'react';
import { toast } from '@/ui/toast';
import {
  createApiKey,
  deleteApiKey,
  disableApiKey,
  enableApiKey,
  listApiKeys,
  updateApiKey,
  type ApiKey,
  type CreateApiKeyResponse,
} from '@/services/api-key';
import {
  getDefaultExpireTime,
  isoToTimestamp,
  timestampToIso,
} from './profile-center-api-key-utils';

export function useProfileCenterApiKeyPanel() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpireAt, setNewKeyExpireAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(
    null
  );
  const [copied, setCopied] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [editName, setEditName] = useState('');
  const [updating, setUpdating] = useState(false);

  const [isToggleStatusDialogOpen, setIsToggleStatusDialogOpen] =
    useState(false);
  const [togglingKey, setTogglingKey] = useState<ApiKey | null>(null);
  const [toggling, setToggling] = useState(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingKey, setDeletingKey] = useState<ApiKey | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (err) {
      setError('获取 API Key 列表失败');
      console.error('获取 API Key 列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchApiKeys();
  }, [fetchApiKeys]);

  const handleOpenCreateModal = useCallback(() => {
    setNewKeyName('');
    setNewKeyExpireAt(timestampToIso(getDefaultExpireTime()));
    setCreatedKey(null);
    setCopied(false);
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setNewKeyName('');
    setNewKeyExpireAt('');
    setCreatedKey(null);
    setCopied(false);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!newKeyName.trim()) {
      toast.error('请输入 API Key 名称');
      return;
    }
    if (!newKeyExpireAt) {
      toast.error('请选择过期时间');
      return;
    }

    try {
      setCreating(true);
      const response = await createApiKey({
        name: newKeyName.trim(),
        expires_at: isoToTimestamp(newKeyExpireAt),
      });
      setCreatedKey(response);
      await fetchApiKeys();
      toast.success('创建成功，请立即复制并保存 API Key');
    } catch (err) {
      toast.error('创建 API Key 失败');
      console.error('创建 API Key 失败:', err);
    } finally {
      setCreating(false);
    }
  }, [fetchApiKeys, newKeyExpireAt, newKeyName]);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('复制失败');
    }
  }, []);

  const handleOpenEditModal = useCallback((key: ApiKey) => {
    setEditingKey(key);
    setEditName(key.name || '');
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingKey(null);
    setEditName('');
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!editingKey) return;
    if (!editName.trim()) {
      toast.error('请输入 API Key 名称');
      return;
    }

    try {
      setUpdating(true);
      await updateApiKey(editingKey.id, { name: editName.trim() });
      toast.success('更新成功');
      await fetchApiKeys();
      handleCloseEditModal();
    } catch (err) {
      toast.error('更新 API Key 失败');
      console.error('更新 API Key 失败:', err);
    } finally {
      setUpdating(false);
    }
  }, [editName, editingKey, fetchApiKeys, handleCloseEditModal]);

  const handleOpenToggleStatusDialog = useCallback((key: ApiKey) => {
    setTogglingKey(key);
    setIsToggleStatusDialogOpen(true);
  }, []);

  const handleCloseToggleStatusDialog = useCallback(() => {
    setIsToggleStatusDialogOpen(false);
    setTogglingKey(null);
  }, []);

  const handleConfirmToggleStatus = useCallback(async () => {
    if (!togglingKey) return;

    try {
      setToggling(true);
      if (togglingKey.status === 'active') {
        await disableApiKey(togglingKey.id);
        toast.success('已禁用');
      } else {
        await enableApiKey(togglingKey.id);
        toast.success('已启用');
      }
      await fetchApiKeys();
      handleCloseToggleStatusDialog();
    } catch (err) {
      toast.error('操作失败');
      console.error('切换 API Key 状态失败:', err);
    } finally {
      setToggling(false);
    }
  }, [fetchApiKeys, handleCloseToggleStatusDialog, togglingKey]);

  const handleOpenDeleteDialog = useCallback((key: ApiKey) => {
    setDeletingKey(key);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeletingKey(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deletingKey) return;

    try {
      setDeleting(true);
      await deleteApiKey(deletingKey.id);
      toast.success('删除成功');
      await fetchApiKeys();
      handleCloseDeleteDialog();
    } catch (err) {
      toast.error('删除 API Key 失败');
      console.error('删除 API Key 失败:', err);
    } finally {
      setDeleting(false);
    }
  }, [deletingKey, fetchApiKeys, handleCloseDeleteDialog]);

  return {
    apiKeys,
    loading,
    error,
    fetchApiKeys,
    isCreateModalOpen,
    newKeyName,
    setNewKeyName,
    newKeyExpireAt,
    setNewKeyExpireAt,
    creating,
    createdKey,
    copied,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleCreate,
    handleCopy,
    isEditModalOpen,
    editingKey,
    editName,
    setEditName,
    updating,
    handleOpenEditModal,
    handleCloseEditModal,
    handleUpdate,
    isToggleStatusDialogOpen,
    setIsToggleStatusDialogOpen,
    togglingKey,
    toggling,
    handleOpenToggleStatusDialog,
    handleCloseToggleStatusDialog,
    handleConfirmToggleStatus,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    deletingKey,
    deleting,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDelete,
  };
}

export type ProfileCenterApiKeyPanelController = ReturnType<
  typeof useProfileCenterApiKeyPanel
>;
